import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { createNavigatorFactory, TabRouter, useNavigationBuilder } from '@react-navigation/native';
import type { BottomTabBarProps, BottomTabNavigationOptions } from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { appTheme } from '../../shared/theme';
import type { TabParamList } from '../types';
import { MAIN_TAB_INITIAL_ROUTE, MAIN_TAB_STYLES, type MainTabModel, type MainTabRoute } from '../../shared/navigation/tabRegistry';
import { getTabBarIcon } from '../mainTabBarIcons/mainTabBarIcons';
import { AndroidFloatingTabBar } from './AndroidFloatingTabBar';
import { getAndroidFloatingTabBarLayoutHeight } from './androidFloatingTabBarLayout';

type Props = {
  model: MainTabModel;
};

type WebTabNavigatorProps = {
  id?: string;
  initialRouteName?: keyof TabParamList;
  children: React.ReactNode;
  screenOptions?: BottomTabNavigationOptions;
  tabBar: (props: BottomTabBarProps) => React.ReactNode;
};

/**
 * PWA tab navigator: mount only the focused scene.
 * `enableScreens(false)` leaves bottom-tabs' inactive absoluteFill scenes visible;
 * a single-scene router avoids stacked tab content on web.
 */
function WebSingleSceneTabNavigator({
  initialRouteName,
  children,
  screenOptions,
  tabBar,
}: WebTabNavigatorProps) {
  const insets = useSafeAreaInsets();
  const { state, navigation, descriptors, NavigationContent } = useNavigationBuilder(TabRouter, {
    children,
    screenOptions,
    initialRouteName: initialRouteName as string | undefined,
  });

  const focused_route = state.routes[state.index];
  const focused_descriptor = focused_route ? descriptors[focused_route.key] : undefined;

  const tab_bar_props = {
    state: state as BottomTabBarProps['state'],
    descriptors: descriptors as BottomTabBarProps['descriptors'],
    navigation: navigation as unknown as BottomTabBarProps['navigation'],
    insets,
  } satisfies BottomTabBarProps;

  return (
    <NavigationContent>
      <View style={styles.flex}>
        <View style={styles.scene}>{focused_descriptor?.render()}</View>
        {tabBar(tab_bar_props)}
      </View>
    </NavigationContent>
  );
}

const createWebTabNavigator = createNavigatorFactory(WebSingleSceneTabNavigator);

const WebTab = createWebTabNavigator<TabParamList>();

const ANDROID_TAB_BAR_LIGHT_BG = '#FFFFFF';

export function MainTabNavigatorPlatform({ model }: Props) {
  const { tab_routes, primary, tab_labels, menu_tab_flow, screen_bg } = model;
  const { labelFontSize, inactiveTintColor } = MAIN_TAB_STYLES.android;
  const insets = useSafeAreaInsets();
  const tab_bar_layout_height = getAndroidFloatingTabBarLayoutHeight(insets.bottom);
  const scene_bg = screen_bg ?? appTheme.colors.background;

  const render_tab_bar = useCallback((props: BottomTabBarProps) => <AndroidFloatingTabBar {...props} />, []);

  const screen_options = useMemo(
    () => ({
      headerShown: false,
      tabBarActiveTintColor: primary ?? appTheme.colors.primary,
      tabBarInactiveTintColor: inactiveTintColor,
      tabBarStyle: {
        position: 'absolute' as const,
        left: 0,
        right: 0,
        bottom: 0,
        height: tab_bar_layout_height,
        backgroundColor: ANDROID_TAB_BAR_LIGHT_BG,
        borderTopWidth: 0,
        elevation: 0,
      },
    }),
    [primary, inactiveTintColor, tab_bar_layout_height],
  );

  const per_screen_options = useMemo(() => {
    const options = {} as Record<
      keyof TabParamList,
      {
        title: string;
        tabBarLabel: string;
        tabBarLabelStyle: { fontSize: number };
        tabBarIcon: ReturnType<typeof getTabBarIcon>;
      }
    >;

    for (const { name } of tab_routes) {
      options[name] = {
        title: tab_labels[name],
        tabBarLabel: tab_labels[name],
        tabBarLabelStyle: { fontSize: labelFontSize },
        tabBarIcon: getTabBarIcon(name, { menu_tab_flow }),
      };
    }

    return options;
  }, [tab_routes, tab_labels, labelFontSize, menu_tab_flow]);

  return (
    <View style={[styles.root, { backgroundColor: scene_bg }]}>
      <WebTab.Navigator
        initialRouteName={MAIN_TAB_INITIAL_ROUTE}
        tabBar={render_tab_bar}
        screenOptions={screen_options}>
        {tab_routes.map(({ name, component }: MainTabRoute) => (
          <WebTab.Screen key={name} name={name} component={component} options={per_screen_options[name]} />
        ))}
      </WebTab.Navigator>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
  },
  flex: {
    flex: 1,
    minHeight: 0,
  },
  scene: {
    flex: 1,
    minHeight: 0,
  },
});
